/**
 * usePlayAds.tsx
 */
import Channel from '@shared/classes/channel';
import Event from '@shared/classes/event';
import User from '@shared/classes/user';
import { AdSdkProvider, PlayAdType } from '@shared/types/enums/ads';
import { PlayerState } from '@shared/types/enums/player';
import { clearPlayAd, setPlayAd } from '@shared/store/slices/ads.slice';
import { mediaSelector } from '@shared/store/slices/media.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Constants.
 *
 * @constant {number} DEFAULT_INITIAL_DELAY_MS - The default initial delay before the first ad is displayed, only used as fallback
 * @constant {number} DEFAULT_PLAY_AD_INTERVAL - The interval at which ads are displayed, only used as fallback
 */
const DEFAULT_INITIAL_DELAY_S = 1 * 60; // 1 minute: BE is returning this value in seconds
const DEFAULT_PLAY_AD_INTERVAL_M = 15; // 15 minutes: BE is returning this interval in minutes
const SECONDS_TO_MS = 1000; // seconds to milliseconds conversion
const MINUTE_TO_MS = 60 * 1000; // minute to milliseconds conversion
const NEXT_EPISODE_MARK = 30; // Reminding seconds required to display NextEpisode

/**
 * Props interface.
 *
 * @interface Props
 * @property {Event} [asset] - Optional event asset to display pause ad information for
 * @property {function} [togglePilot] - Optional function to toggle the pilot display
 */
interface Props {
  asset?: Event;
  live?: boolean;
  togglePilot?: (display: boolean) => void;
}

/**
 * Custom hook that manages pause ad display logic.
 * Fetches and displays pause ads when the player is paused, and clears the modal when playback resumes.
 *
 * @param {Event} [asset] - Optional event asset to display pause ad information for
 */
export default function usePlayAds({ asset, live, togglePilot }: Props) {
  const dispatch = useAppDispatch();
  const { duration, position, state } = useAppSelector(mediaSelector);
  const { planId } = useAppSelector(userSelector);

  // Retrieve user plan
  const assetRef = useRef<Event | undefined>(undefined);
  const channelRef = useRef<Channel | undefined>(undefined);
  const configRef = useRef<ReturnType<typeof getAdConfig>>(null);
  const planRef = useRef(User.getPlan(planId));
  planRef.current = User.getPlan(planId);

  // State
  const [nextImpression, setNextImpression] = useState<Date | null>(null);

  // ------------------------------
  // Callbacks
  // ------------------------------

  const getAdConfig = useCallback(() => {
    // 1. Check if play is enabled in plan: If not, return
    const planAds = planRef.current?.advertisement?.sections;
    const playType = live ? PlayAdType.L_LIVE : PlayAdType.L_VOD;
    const planPlay = planAds?.vastTags[playType];
    if (!planPlay?.enabled) return null;

    // 2. Check if play is enabeld in channel: If not, return
    const channelAds = assetRef.current?.channel?.advertisement;
    const channelPlay = channelAds?.services?.vastTags?.[playType];
    if (!channelPlay?.enabled) return null;

    // 3. Check the provider: Only TEADS is available now
    const channelProvider = channelPlay?.sdkProvider;
    const planProvider = planPlay?.sdkProvider;

    // 3.1 Check channel
    if (
      channelProvider?.name === AdSdkProvider.TEADS &&
      channelProvider?.placementId
    ) {
      const adsWindow = channelAds?.adsWindow;
      return { ...channelProvider, adsWindow };
    }
    // 3.2 Check plan
    else if (
      planProvider?.name === AdSdkProvider.TEADS &&
      planProvider?.placementId
    ) {
      const adsWindow = planAds?.adsWindow;
      return { ...planProvider, adsWindow };
    }

    // No ad found:
    return null;
  }, []);

  const triggerPlayAd = useCallback(() => {
    if (!configRef.current) {
      setNextImpression(null);
      return;
    }
    // Initialize playAd
    dispatch(
      setPlayAd({
        active: false,
        asset: assetRef.current,
        adsWindow: configRef.current?.adsWindow,
        placementId: configRef.current?.placementId,
        sdkProvider: AdSdkProvider.TEADS,
      })
    );
    // Calculate next impression
    const currentDate = new Date();
    const offset =
      (configRef.current.requestAdInterval || DEFAULT_PLAY_AD_INTERVAL_M) *
      MINUTE_TO_MS;
    setNextImpression(new Date(currentDate.getTime() + offset));
  }, []);

  // ------------------------------
  // Effect hooks
  // ------------------------------

  // Initialization & asset update
  useEffect(() => {
    // Check if asset has changed
    const isNewChannel =
      !channelRef.current || channelRef?.current?.id !== asset?.channel?.id;
    // If new channel or not set or not live, proceed
    if (isNewChannel || !live) {
      // Clear playAd, retrieve new channel and get new config
      dispatch(clearPlayAd());
      assetRef.current = asset;
      channelRef.current = asset?.channel;
      configRef.current = getAdConfig();
      // Calculate next impression
      if (!configRef.current) return;
      const currentDate = new Date();
      const offset =
        (configRef.current?.initialDelayTime || DEFAULT_INITIAL_DELAY_S) *
        SECONDS_TO_MS;
      setNextImpression(new Date(currentDate.getTime() + offset));
    }
  }, [asset, live]);

  // Player state change behaviour
  useEffect(() => {
    const endOffset =
      configRef.current?.adsWindow?.maxDuration ?? 0 + NEXT_EPISODE_MARK;
    const isNearEnd = !live && duration - position <= endOffset;
    const isPlaying = state === PlayerState.PLAYING;
    if (!isNearEnd && isPlaying && nextImpression) {
      const currentDate = new Date();
      const nextDate = new Date(nextImpression);
      if (currentDate > nextDate) {
        triggerPlayAd();
        togglePilot?.(false);
      }
    }
  }, [duration, position, state]);

  // Clear ad state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearPlayAd());
    };
  }, []);
}
